import type { ReactNode } from "react";
import {
  Children,
  isValidElement,
  useState,
  useSyncExternalStore,
} from "react";
import type {
  AnyRouteProps,
  CurrentUrlReturn,
  RouteComponent,
  RouteNode,
  UrlParams,
} from "./types";
import { routes } from "../../../shared/uiRoutes/routes";
import {
  extractTokens,
  isCaseNode,
  isRouteNode,
  normalizeRoutes,
  type QueryToken,
} from "../../../shared/uiRoutes/definition";
export { oneOf, route } from "../../../shared/uiRoutes/definition";
export { routes } from "../../../shared/uiRoutes/routes";

type CompiledPattern = {
  pattern: string;
  matcher: URLPattern;
  searchTokens: QueryToken[];
  // Literal query segments (`k=v` rather than `k=:t`); the URL must carry them
  // for the pattern to match.
  searchLiterals: Array<[string, string]>;
};

export const useCurrentUrl = <T extends RouteNode>(
  scope: T,
  where?: Partial<CurrentUrlReturn<T>>
): CurrentUrlReturn<T> => {
  const patterns = collectPatterns(scope);
  const location = useLocationState();
  let match = findMatch(patterns, location);

  if (
    match &&
    where &&
    !Object.entries(where).every(([k, v]) => match![k] === v)
  ) {
    match = null;
  }

  // Seed with the initial match so a first render that matches doesn't trigger
  // a render-phase update — the fallback below is only read when `match` is null.
  const [lastMatch, setLastMatch] = useState<Record<string, string> | null>(
    match
  );

  // If the URL no longer matches (or matches a different `where`), return last
  // known values to avoid crashes during concurrent rendering before Route
  // unmounts (see #969), and so a covered modal keeps its params.
  if (!match) {
    return (lastMatch ?? {}) as CurrentUrlReturn<T>;
  }

  // `findMatch` returns a fresh object each render, so guard on value equality
  // to avoid an update loop.
  if (!shallowEqualParams(lastMatch, match)) setLastMatch(match);

  // Apply defaults from the first pattern (the scope itself)
  if (patterns.length > 0 && patterns[0]) {
    const compiled = patterns[0];
    const defaults = new Map<string, string>();
    for (const { token, defaultValue } of compiled.searchTokens) {
      if (defaultValue !== undefined && !(token in match)) {
        defaults.set(token, defaultValue);
      }
    }

    // Check if URL contains default values and normalize if needed. A pattern
    // with literal segments (a oneOf case) is page-agnostic, so it never
    // rewrites the URL — it doesn't know the page.
    const hasDefaults = compiled.searchTokens.some(
      ({ token, defaultValue }) =>
        defaultValue && match![token] === defaultValue
    );
    if (hasDefaults && compiled.searchLiterals.length === 0) {
      const normalizedUrl = buildUrl(normalizeParams(match), compiled);
      if (normalizedUrl !== location) {
        history.replaceState(history.state, "", normalizedUrl);
      }
    }

    return { ...Object.fromEntries(defaults), ...match } as CurrentUrlReturn<T>;
  }

  return match as CurrentUrlReturn<T>;
};

const makeUrl = <T extends RouteNode>(
  scope: T | null,
  params?: UrlParams<T> | Record<string, string | number>,
  current: ReturnType<typeof splitUrl> = currentLocationParts()
): string => {
  // A oneOf case is page-agnostic and addressed absolutely: its params
  // translate to the bracket form, any of its params not given are cleared,
  // and the URL is built from whatever page is current.
  if (scope && isCaseNode(scope)) {
    const namespaced: Record<string, string> = {
      [scope.discriminant]: scope.caseName,
    };
    const search = new URLSearchParams(current.search);
    for (const key of search.keys()) {
      if (key.startsWith(`${scope.discriminant}[`)) namespaced[key] = "";
    }
    for (const [k, v] of Object.entries(normalizeParams(params))) {
      namespaced[`${scope.discriminant}[${k}]`] = v;
    }
    return makeUrl(null, namespaced, current);
  }
  const compiled = scope
    ? compilePattern(scope.pattern)
    : currentCompiledPattern(current);
  const ownTokenKeys = scope ? extractOwnTokenKeys(scope) : null;
  const related = scope ? collectPatterns(scope) : [compiled];
  const mergedParams = {
    ...getCurrentParams(compiled, ownTokenKeys, related, current),
    ...normalizeParams(params),
  };
  return buildUrl(mergedParams, compiled);
};

// Rendered links must subscribe to location so memoization tracks inherited URL parameters.
export function useMakeUrl() {
  const current = splitUrl(useLocationState());
  return <T extends RouteNode>(
    scope: T | null,
    params?: UrlParams<T> | Record<string, string | number>,
    options?: { inheritParams?: boolean }
  ) =>
    makeUrl(
      scope,
      params,
      options?.inheritParams === false ? splitUrl("/") : current
    );
}

function extractOwnTokenKeys(scope: RouteNode): Set<string> {
  if (!scope.ownTail) return new Set();
  const { search } = splitUrl(`/${scope.ownTail}`);
  return new Set(extractTokens(search).map(({ key }) => key));
}

function currentLocationParts() {
  if (typeof location === "undefined") {
    return { pathname: "/", search: "", hash: "" };
  }
  return location;
}

function currentCompiledPattern(
  current: ReturnType<typeof splitUrl>
): CompiledPattern {
  for (const root of appRoutes) {
    for (const compiled of collectPatterns(root)) {
      if (compiled.matcher.exec(current)) return compiled;
    }
  }
  return compilePattern(current.pathname);
}

function getCurrentParams(
  compiled: CompiledPattern,
  ownTokenKeys: Set<string> | null,
  related: CompiledPattern[],
  target: ReturnType<typeof splitUrl>
): Record<string, string> {
  const params: Record<string, string> = {};
  const match = compiled.matcher.exec(target);
  if (match) {
    for (const [key, value] of Object.entries(match.pathname.groups)) {
      if (value === undefined || /^\d+$/.test(key)) continue;
      params[key] = value;
    }
  }
  const searchValues = target.search
    ? new URLSearchParams(target.search)
    : null;
  const withinScope = related.some((p) => p.matcher.exec(target));
  for (const { key, token } of compiled.searchTokens) {
    // When navigating to a different route outside this scope, don't carry
    // over search params owned by the destination scope — they belong to that
    // route only and would leak across unrelated lists. Inherited params
    // (e.g. the root-level `modal` params) still persist.
    if (!withinScope && ownTokenKeys?.has(key)) continue;
    const value = searchValues?.get(key);
    if (value !== null && value !== undefined) params[token] = value;
  }
  return params;
}

export function navigate<T extends RouteNode>(
  scope: T | null,
  params?: UrlParams<T> | Record<string, string | number>,
  options?: { replace?: boolean; state?: unknown }
) {
  const method = options?.replace ? "replaceState" : "pushState";
  history[method](
    options && "state" in options ? options.state : history.state,
    "",
    makeUrl(scope, params)
  );
}

export function useRouteMatch<T extends RouteNode | undefined>(
  scope?: T,
  params?: Record<string, string | number>
): [boolean, Record<string, string>] {
  const location = useLocationState();
  if (!scope) return [true, {}];
  const match = findMatch(collectPatterns(scope), location);
  if (!match) return [false, {}];
  if (params) {
    const paramsMatch = Object.entries(params).every(
      ([key, value]) => match[key] === String(value)
    );
    if (!paramsMatch) return [false, match];
  }
  return [true, match];
}

export const Route: RouteComponent = ({ path, children }: AnyRouteProps) => {
  const location = useLocationState();
  const match = path ? findMatch(collectPatterns(path), location) : null;
  if (!path) return typeof children === "function" ? children() : children;
  if (!match) return null;
  if (typeof children === "function") return children(match);
  return children;
};

export function Switch({ children }: { children: ReactNode }) {
  const location = useLocationState();
  for (const child of Children.toArray(children)) {
    if (!isValidElement<AnyRouteProps>(child) || child.type !== Route) continue;
    const { path } = child.props;
    const match = path ? findMatch(collectPatterns(path), location) : {};
    if (!match) continue;
    const content = child.props.children;
    return typeof content === "function"
      ? path
        ? content(match)
        : (content as () => ReactNode)()
      : content;
  }
  return null;
}

function collectPatterns(scope: RouteNode) {
  const patterns = [compilePattern(scope.pattern)];
  for (const value of Object.values(scope)) {
    if (isRouteNode(value)) {
      patterns.push(...collectPatterns(value));
    }
  }
  return patterns;
}

function shallowEqualParams(
  a: Record<string, string> | null,
  b: Record<string, string> | null
) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => a[k] === b[k]);
}

function findMatch(patterns: CompiledPattern[], location: string) {
  const target = splitUrl(location);
  const searchValues = target.search
    ? new URLSearchParams(target.search)
    : null;
  for (const compiled of patterns) {
    const match = compiled.matcher.exec(target);
    if (!match) continue;
    if (compiled.searchLiterals.some(([k, v]) => searchValues?.get(k) !== v)) {
      continue;
    }
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(match.pathname.groups)) {
      if (value === undefined || /^\d+$/.test(key)) continue;
      params[key] = value;
    }
    for (const { key, token } of compiled.searchTokens) {
      const value = searchValues?.get(key);
      if (value !== null && value !== undefined) params[token] = value;
    }
    return params;
  }
  return null;
}

function buildUrl(params: Record<string, string>, compiled: CompiledPattern) {
  const { pattern } = compiled;
  // Create a map of token -> defaultValue
  const defaults = new Map<string, string>();
  for (const { token, defaultValue } of compiled.searchTokens) {
    if (defaultValue !== undefined) defaults.set(token, defaultValue);
  }

  let url = pattern.replace(/:([A-Za-z0-9_[\]]+)(?:=[^&?]*)?/g, (_, key) => {
    const value = params[key];
    const defaultValue = defaults.get(key);
    // Omit if undefined, empty string, or matches default
    if (value === undefined || value === "" || value === defaultValue)
      return "";
    return encodeURIComponent(value);
  });

  // Clean up empty query params and consecutive separators
  url = url
    .replace(/([?&])[^=]+=(?=&|$)/g, "$1") // Remove empty params
    .replace(/[?&]+/g, (match) => (match.includes("?") ? "?" : "&")) // Collapse consecutive separators
    .replace(/[?&]$/, "") // Remove trailing separator
    .replace(/\?&/, "?"); // Fix ?& to just ?

  return url;
}

function normalizeParams(params?: Record<string, string | number>) {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)])
  );
}

function splitUrl(target: string) {
  const [pathAndSearch = "", hashPart = ""] = target.split("#", 2);
  const [pathnameRaw = "/", searchPart = ""] = pathAndSearch.split("?", 2);
  const pathname = pathnameRaw || "/";
  const search = searchPart ? `?${searchPart}` : undefined;
  const hash = hashPart ? `#${hashPart}` : undefined;
  return { pathname, search, hash };
}

const appRoutes: RouteNode[] = [routes];

export function defineRoutes<T extends Record<string, RouteNode>>(config: T) {
  appRoutes.push(...Object.values(config));
  return normalizeRoutes(config);
}

const compiledPatternCache = new Map<string, CompiledPattern>();

function compilePattern(pattern: string): CompiledPattern {
  const cached = compiledPatternCache.get(pattern);
  if (cached) return cached;
  const parts = splitUrl(pattern);
  const compiled: CompiledPattern = {
    pattern,
    matcher: new URLPattern({ pathname: parts.pathname }),
    searchTokens: extractTokens(parts.search),
    searchLiterals: extractLiterals(parts.search),
  };
  compiledPatternCache.set(pattern, compiled);
  return compiled;
}

function extractLiterals(part?: string): Array<[string, string]> {
  if (!part) return [];
  const literals: Array<[string, string]> = [];
  for (const segment of part.replace(/^[?#]/, "").split("&")) {
    const eqIndex = segment.indexOf("=");
    if (eqIndex <= 0) continue;
    const value = segment.slice(eqIndex + 1);
    if (value && !value.startsWith(":")) {
      literals.push([segment.slice(0, eqIndex), value]);
    }
  }
  return literals;
}

function useLocationState() {
  const value = () => `${location.pathname}${location.search}${location.hash}`;
  return useSyncExternalStore(subscribeLocation, value, value);
}

function subscribeLocation(listener: () => void) {
  const events = ["popstate", "hashchange"];
  for (const event of events) addEventListener(event, listener);
  return () => {
    for (const event of events) removeEventListener(event, listener);
  };
}

export function useHistoryEntryKey() {
  const value = (): string => history.state?.entryKey ?? "";
  return useSyncExternalStore(subscribeLocation, value, value);
}

const beforeNavigationListeners = new Set<() => void>();

export function beforeNavigation(listener: () => void) {
  beforeNavigationListeners.add(listener);
  return () => {
    beforeNavigationListeners.delete(listener);
  };
}

function saveBeforeNavigation() {
  for (const listener of beforeNavigationListeners) listener();
}

export function useCurrentLocation() {
  return useLocationState();
}

const visitedLocations: string[] = [];

export function lastVisitedLocation(predicate: (url: string) => boolean) {
  return visitedLocations.findLast(predicate);
}

export function isRouteLocation(scope: RouteNode, url: string) {
  return findMatch([compilePattern(scope.pattern)], url) !== null;
}

type DeriveHistoryState = (url: string, state: unknown) => unknown;
const stateDerivations: DeriveHistoryState[] = [];

// A module may derive each new history entry's state from the URL being
// navigated to (the modal stack maintains itself this way). Only state carried
// through unchanged is derived; state passed explicitly is trusted as-is.
export function deriveHistoryState(derive: DeriveHistoryState) {
  stateDerivations.push(derive);
}

let navHooksInstalled = false;
export function installBrowserNavigation() {
  if (navHooksInstalled) return;
  navHooksInstalled = true;
  const originals = {
    pushState: history.pushState.bind(history),
    replaceState: history.replaceState.bind(history),
  };
  originals.replaceState(
    { ...history.state, entryKey: crypto.randomUUID() },
    "",
    location.href
  );
  history.scrollRestoration = "manual";
  addEventListener("popstate", saveBeforeNavigation);
  const commit = (method: "pushState" | "replaceState") =>
    function (state: unknown, title: string, url?: string | URL | null) {
      saveBeforeNavigation();
      const current = `${location.pathname}${location.search}${location.hash}`;
      const target = url == null ? current : String(url);
      const derived =
        state === history.state
          ? stateDerivations.reduce((s, derive) => derive(target, s), state)
          : state;
      // One rule: navigating to the URL you are already on edits the current
      // entry instead of growing history.
      const commitAs =
        method === "pushState" && target !== current
          ? "pushState"
          : "replaceState";
      // A replaced entry was only passed through, such as a page that redirects away when you can’t access it, so it isn’t a place to return to.
      if (commitAs === "pushState" && visitedLocations.at(-1) !== current)
        visitedLocations.push(current);
      originals[commitAs](
        {
          ...(derived as Record<string, unknown>),
          entryKey:
            target === current
              ? (history.state?.entryKey ?? crypto.randomUUID())
              : crypto.randomUUID(),
        },
        title,
        url
      );
      dispatchEvent(new PopStateEvent("popstate"));
    };
  history.pushState = commit("pushState");
  history.replaceState = commit("replaceState");
}
